import { memo, useEffect, useState } from 'react';

import { getRouteApi } from '@mint/routing';

import { Box, Pagination } from '@myn/mui';

import {
  useGetCo2OrdersMetaQuery,
  useGetCo2OrdersQuery,
} from '../../api/cO2Slice.js';
import Co2NoContent from '../../features/Co2NoContent/index.js';
import Co2NotificationsHeader from '../../features/Co2NotificationsHeader/Co2NotificationsHeader.jsx';
import { NewCo2NotificationRow } from '../../features/NewCo2NotificationRow/NewCo2NotificationRow.jsx';
import { useCampaignDataSelector } from '../../hooks/useGetCampaignData.js';
import CarbonNeutralTemplate from '../../templates/CarbonNeutralTemplate/CarbonNeutralTemplate.jsx';
import {
  Co2Filter,
  Co2OrderStatus,
  GetCo2OrdersRes,
} from '../../types/co2notifications.js';

const PAGE_SIZE = 6;

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const CarbonNeutralPage = () => {
  const { campaignId: campaignCollectionId } = routeApi.useParams();
  const { data: currency } = useCampaignDataSelector('currency');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [ordersState, setOrdersState] = useState<GetCo2OrdersRes>();
  const [filter, setFilter] = useState<Co2Filter>({
    search: '',
    show: {
      name: '',
      value: '',
    },
    sortBy: {
      name: '',
      value: '',
    },
    selected: false,
  });

  const { data: ordersMeta, isLoading: isMetaLoading } =
    useGetCo2OrdersMetaQuery();

  const params = {
    campaignCollectionId: Number(campaignCollectionId),
    page: currentPage,
    pageSize: PAGE_SIZE,
    sortBy: filter.sortBy.value
      ? filter.sortBy.value
      : (ordersMeta?.filters.sortBy[0].name ?? ''),
    statusFilter: filter.show.value
      ? filter.show.value
      : (ordersMeta?.filters.statusFilters[0].name ?? ''),
    mediaPlanName: filter.search,
  };

  const {
    data: ordersData,
    isLoading: isOrdersLoading,
    refetch: refetchOrdersData,
  } = useGetCo2OrdersQuery(params, {
    skip: !ordersMeta || isMetaLoading,
  });

  const onSuccessOrdersRequest = (id: number, type: 'confirm' | 'reject') => {
    setOrdersState((prevState) => {
      if (prevState) {
        const newOrdersState = {
          ...prevState,
          orders: prevState.orders.map((order) => {
            if (order.id === id) {
              return {
                ...order,
                orderStatus:
                  type === 'confirm'
                    ? Co2OrderStatus.Booked
                    : Co2OrderStatus.Rejected,
              };
            }
            return order;
          }),
        };
        return newOrdersState;
      }
      return prevState;
    });
  };

  useEffect(() => {
    if (ordersMeta) {
      setCurrentPage(1);
      setFilter({
        search: '',
        show: {
          name: ordersMeta.filters.statusFilters[0].label,
          value: ordersMeta.filters.statusFilters[0].name,
        },
        sortBy: {
          name: ordersMeta.filters.sortBy[0].label,
          value: ordersMeta.filters.sortBy[0].name,
        },
        selected: false,
      });
    }
  }, [ordersMeta]);

  useEffect(() => {
    if (ordersState && ordersMeta && !isOrdersLoading) {
      refetchOrdersData();
    }
  }, [filter, currentPage]);

  useEffect(() => {
    if (ordersData) {
      setOrdersState(ordersData);
    }
  }, [ordersData]);

  const isNoContent =
    (!ordersState?.orders.length && !filter.selected) || isMetaLoading;

  const isNotFoundContent =
    !ordersState?.orders.length && !isMetaLoading && filter.selected;

  const handleFiltersChange = (filter: Co2Filter) => {
    setFilter({ ...filter, selected: true });
  };

  const shouldHideHeader = isNoContent && !isNotFoundContent;

  const shouldRenderNoContent = isNoContent || isNotFoundContent;

  const shouldHidePagination = isNoContent || isNotFoundContent;

  return (
    <CarbonNeutralTemplate
      header={
        shouldHideHeader ? null : (
          <Co2NotificationsHeader
            filter={filter}
            ordersMeta={ordersMeta}
            onChange={handleFiltersChange}
          />
        )
      }
      notificationList={
        <Box>
          {shouldRenderNoContent ? (
            <Co2NoContent
              noFoundContent={isNotFoundContent}
              search={filter.search}
            />
          ) : (
            ordersState?.orders.map((order) => (
              <NewCo2NotificationRow
                key={order.id}
                item={order}
                currency={currency || null}
                onSuccess={onSuccessOrdersRequest}
              />
            ))
          )}
        </Box>
      }
      shouldHidePagination={shouldHidePagination}
      pagination={
        <Pagination
          onChange={(event, value) => setCurrentPage(value)}
          page={currentPage}
          count={ordersState?.lastPage ? ordersState.lastPage : 1}
          boundaryCount={1}
        />
      }
    />
  );
};

export default memo(CarbonNeutralPage);
